import styled from "styled-components";
import CultureBackground from "/public/assets/images/career/culture/culture-bg.png"
import CultureMoBackground from "/public/assets/images/career/culture/culture-mo-bg.png"
import CultureItem01 from "/public/assets/images/career/culture/item01.png"
import CultureItem02 from "/public/assets/images/career/culture/item02.png"
import CultureItem03 from "/public/assets/images/career/culture/item03.png"
import CultureItem04 from "/public/assets/images/career/culture/item04.png"
import CultureItem05 from "/public/assets/images/career/culture/item05.png"
import CultureItem06 from "/public/assets/images/career/culture/item06.png"
import CultureItem07 from "/public/assets/images/career/culture/item07.png"
import {useContext, useEffect, useRef} from "react";
import {LayoutContext} from "@/app/ClientRootHomepage";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getScroller, setupLenyStyle, refreshOnAssetsReady } from "@/app/homepage/utils";
import {SIZES} from "@/components/constants";
import {useMediaQuery} from "react-responsive";

gsap.registerPlugin(ScrollTrigger);

const Culture = () => {
    const { rootRef } = useContext(LayoutContext);
    const wrapRef = useRef(null);
    const isTablet = useMediaQuery({
        query: `(max-width: ${SIZES.TABLET})`
    });
    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;

        const scroller = getScroller(rootRef);
        const contentEl =
            scroller?.querySelector?.(".root") ||
            scroller?.firstElementChild ||
            scroller;

        const { cleanup } = setupLenyStyle({ wrap, scroller, contentEl });

        const triggers = [];
        const timelines = [];

        // 섹션별 진입: .item-title → .item-list 순차 등장
        const sections = Array.from(wrap.querySelectorAll(".item"));

        sections.forEach((section) => {
            const titleEl = section.querySelector(".item-title");
            const cards = Array.from(section.querySelectorAll(".item-list"));
            if (titleEl) {
                gsap.set(titleEl, {
                    opacity: 0,
                    y: 16,
                });
            }

            if (cards.length) {
                gsap.set(cards, {
                    opacity: 0,
                    y: 30,
                });
            }

            const tl = gsap.timeline({ paused: true });

            if (titleEl) {
                tl.to(
                    titleEl,
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.5,
                        ease: "power2.out",
                        force3D: false,
                    },
                    0
                );
            }

            if (cards.length) {
                tl.to(
                    cards,
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        ease: "power2.out",
                        stagger: 0.12,
                        force3D: false,
                    },
                    titleEl ? ">-0.15" : 0
                );
            }

            const st = ScrollTrigger.create({
                trigger: section,
                start: "top 70%",
                end: "top 45%",
                toggleActions: "play none none reverse",
                onEnter: () => tl.play(),
                onEnterBack: () => tl.play(),
                onLeaveBack: () => tl.reverse(),
            });

            timelines.push(tl);
            triggers.push(st);
        });

        const onResize = () => ScrollTrigger.refresh();
        window.addEventListener("resize", onResize);

        const offAssets = refreshOnAssetsReady(wrap, () => {
            ScrollTrigger.refresh();
        });

        return () => {
            triggers.forEach((t) => t.kill());
            timelines.forEach((t) => t.kill());
            window.removeEventListener("resize", onResize);
            offAssets && offAssets();
            cleanup();
        };
    }, [rootRef]);


    return(
        <CultureWrap ref={wrapRef}>
            <TitleWrap>
                {
                    !isTablet ?

                        <div className={"text-box"}>
                            <h2>Culture</h2>
                            <p>
                                함께 성장하고, 함께 만들어가는 우리의 문화
                            </p>
                        </div>
                        :
                        <div className={"mo-value-box"}>
                            <div className={"box-text-inner"}>
                                <h2>Culture</h2>
                                <p>
                                    함께 성장하고, 함께 만들어가는 우리의 문화
                                </p>
                            </div>
                            <div className={"box-bg-inner"}>
                                <img src={CultureMoBackground?.src} alt={"배너 이미지"}/>
                            </div>
                        </div>
                }
            </TitleWrap>
            <ContentWrap>
                <div className={"item"}>
                    <div className={"item-title"}>1. 끝없는 성장의 여정</div>
                    <div className={"item-wrap"}>
                        <div className={"item-list"}>
                            <div className={"item-bg"}>
                                <img src={CultureItem01?.src} />
                            </div>
                            <div className={"item-text-box"}>
                                <p>지식으로 하나되는 시간</p>
                                <h2>북클럽</h2>
                                <span>함께 선정한 책을 통해 새로운 시각을 나누고, 서로의 생각을 공유합니다. 단순한 독서모임을 넘어, 동료들과 함께 성장하는 소중한 배움의 순간들을 만들어가고 있습니다.</span>
                            </div>
                        </div>
                        <div className={"item-list"}>
                            <div className={"item-bg"}>
                                <img src={CultureItem02?.src}/>
                            </div>
                            <div className={"item-text-box"}>
                                <p>도전하는 용기, 성장하는 기쁨</p>
                                <h2>OKR</h2>
                                <span>명확한 목표 설정과 진솔한 피드백을 통해 개인과 회사가 함께 성장합니다. 실패를 두려워하지 않고 도전할 수 있는 환경에서, 매 순간이 성장의 기회가 됩니다.</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"item"}>
                    <div className={"item-title"}>2. 진정한 협업의 힘</div>
                    <div className={"item-wrap"}>
                        <div className={"item-list"}>
                            <div className={"item-bg"}><img src={CultureItem03?.src}/></div>
                            <div className={"item-text-box"}>
                                <p>일과 즐거움이 만나는 곳</p>
                                <h2>협업워크샵</h2>
                                <span>다양한 액티비티와 창의적인 프로그램을 통해 직원들과 더 깊이 소통합니다. 함께 웃고, 함께 고민하며 쌓아가는 진짜 팀워크를 경험해보시기 바랍니다.</span>
                            </div>
                        </div>
                        <div className={"item-list"}>
                            <div className={"item-bg"}><img src={CultureItem04?.src}/></div>
                            <div className={"item-text-box"}>
                                <p>취미로 이어지는 특별한 인연</p>
                                <h2>동호회</h2>
                                <span>업무를 넘어 개인의 관심사와 취미를 공유하며 자연스럽게 형성되는 동료애. 같은 열정을 나누는 사람들과 함께하는 시간은 직장을 넘어 인생의 소중한 추억이 됩니다.</span>
                            </div>
                        </div>
                        <div className={"item-list"}>
                            <div className={"item-bg"}><img src={CultureItem05?.src}/></div>
                            <div className={"item-text-box"}>
                                <p>우리가 직접 만드는 일터</p>
                                <h2>문화개선위원회</h2>
                                <span>직원들이 주도적으로 참여하여 더 나은 조직문화를 만들어갑니다. 모든 구성원의 목소리가 존중받고, 실제 변화로 이어지는 진정한 수평적 문화를 지향합니다.</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={"item"}>
                    <div className={"item-title"}>3. 세상을 향한 따뜻한 마음</div>
                    <div className={"item-wrap"}>
                        <div className={"item-list"}>
                            <div className={"item-bg"}><img src={CultureItem06?.src}/></div>
                            <div className={"item-text-box"}>
                                <h2>건강한 내일을 위한 약속</h2>
                                <span>최고 품질의 진단제품과 바이오 연구제품을 통해 국민 건강 증진에 기여합니다. 우리의 전문성과 혁신이 더 건강한 사회를 만드는 작은 씨앗이 되고 있습니다.</span>
                            </div>
                        </div>
                        <div className={"item-list"}>
                            <div className={"item-bg"}><img src={CultureItem07?.src}/></div>
                            <div className={"item-text-box"}>
                                <h2>
                                    {
                                        !isTablet ?
                                            <>비투게더edu-희망을 전하는 교육나눔</>
                                            :
                                            <>
                                                비투게더edu<br/>-희망을 전하는 교육나눔
                                            </>
                                    }
                                </h2>
                                <span>저소득층과 다문화 가정 자녀들에게 양질의 교육 기회를 제공합니다. 아이들의 꿈이 경제적 어려움으로 포기되지 않도록, 우리의 작은 관심이 큰 변화의 시작이 됩니다.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </ContentWrap>
        </CultureWrap>
    )
}
const ContentWrap = styled.div`
    padding: 80px 0 0;
    max-width: 1240px;
    margin: 0 auto;
    .item {
        & + .item {
            margin-top: 160px;
        }
        .item-title{
            font-weight: 700;
            font-size: 48px;
            line-height: 58px;
            letter-spacing: -0.02em;
            color: #23203B;
            margin-bottom: 40px;
        }
        .item-wrap{
            display: flex;
            gap: 40px;
            .item-list{
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                border: 1px solid #E6E6E6;
                border-radius: 20px;
                overflow: hidden;
                .item-text-box{
                    padding: 24px 24px 40px;
                    p{
                        font-weight: 600;
                        font-size: 18px;
                        line-height: 28px;
                        letter-spacing: -0.02em;
                        color: #4939B1;
                        margin-bottom: 8px;
                    }
                    h2{
                        font-weight: 700;
                        font-size: 24px;
                        line-height: 36px;
                        letter-spacing: -0.02em;
                        color: #000000;
                        margin-bottom: 24px;
                    }
                    span{
                        font-weight: 400;
                        font-size: 16px;
                        line-height: 24px;
                        color: #333333;
                    }
                }
            }
        }
    }
    @media (max-width: ${SIZES.TABLET}) {
        padding: 80px 16px 0;
        width: 100%;
        max-width: 100%;
        .item {
            & + .item {
                margin-top: 80px;
            }
            .item-title{
                font-size: 32px;
                line-height: 40px;
                letter-spacing: -0.02em;
                color: #23203B;
                margin-bottom: 24px;
            }
            .item-wrap{
                flex-direction: column;
                .item-list{
                    .item-text-box{
                        padding: 24px 16px 40px;
                        p{
                            font-weight: 600;
                            font-size: 18px;
                            line-height: 28px;
                            letter-spacing: -0.02em;
                        }
                        h2{
                            font-size: 24px;
                            line-height: 36px;
                            letter-spacing: -0.02em;
                        }
                        span{
                            font-weight: 400;
                            font-size: 16px;
                            line-height: 24px;
                        }
                    }
                }
            }
        }
    }
`

const CultureWrap = styled.div`
    padding: 60px 0 200px;
    @media (max-width: ${SIZES.TABLET}) {
        padding: 40px 0 160px;
    }
`
const TitleWrap = styled.div`
    padding: 0 114px 80px;

    .text-box {
        border-radius: 20px;
        overflow: hidden;
        background: url(${CultureBackground?.src}) no-repeat center / cover;
        height: 400px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        h2{
            font-weight: 700;
            font-size: 48px;
            line-height: 58px;
            text-align: center;
            letter-spacing: -0.02em;
            color: #FFFFFF;
            margin-bottom: 16px;
        }
        p{
            font-weight: 400;
            font-size: 24px;
            line-height: 36px;
            text-align: center;
            letter-spacing: -0.02em;
            color: #E6E6E6;
        }
    }
    @media (max-width: ${SIZES.TABLET}) {
        padding: 0 0 80px;
        .mo-value-box{
            .box-text-inner{
                text-align: center;
                margin-bottom: 40px;
                h2{
                    font-weight: 700;
                    font-size: 24px;
                    line-height: 36px;
                    letter-spacing: -0.02em;
                    color: #4939B1;
                    margin-bottom: 40px;
                }
                p{
                    font-weight: 400;
                    font-size: 18px;
                    line-height: 28px;
                    color: #666666;
                }
            }
        }
    }
`
export default Culture;
